import type { BatchItemClip } from '@borradh-workspace/api-client/types';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Check,
  Film,
  GripVertical,
  Loader2,
  Mic,
  Music2,
  Pencil,
  Play,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useFlag } from '@/components/posthog-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClipPickerDialog } from '@/features/assistant/_components/rich/clip-picker-dialog';
import { apiClient } from '@borradh-workspace/api-client';
import { useQuery } from '@tanstack/react-query';

import { useArtifactPanel } from '@/features/assistant/_components/artifact-panel-context';
import { ArtifactRow } from '@/features/assistant/_components/artifact-row';
import {
  useHasComposerGate,
  useRegisterComposerGate,
} from '@/features/assistant/_components/composer-gate-context';
import { DecisionFooter } from '@/features/assistant/_components/rich/decision-footer';
import { VideoPreviewDialog } from '@/features/assistant/_components/rich/video-preview-dialog';
import {
  useApplyVideoEdits,
  useBatchItemClips,
  useDiscardVideoEdits,
  useStageItemClips,
  useStageItemMusic,
} from '@/features/content-batches/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { VideoThumbnail } from '@/routes/_authed/create-video/$templateId/-components/shared/video-thumbnail';
import { isClipListCardHistorical } from './clip-list-card-history';
import { ContentMusicEditor } from './content-music-editor';
import { CLAIRE_CONTENT_MUSIC_EDITOR_FLAG } from './feature-flags';
import { NarrationChoiceDialog } from './narration-choice-dialog';
import { ScriptEditorDialog } from './script-editor-dialog';

interface ClipListEditorProps {
  gateId?: string;
  onCancelled?: () => void;
  onDiscarded?: () => void;
  /**
   * The CONTENT ITEM, not the video.
   *
   * Everything this card writes goes to the item, because the item is what
   * survives the fork an approved edit causes. Addressing the video instead is
   * the mistake five earlier attempts made: the fork's id ended up somewhere
   * only the browser could see, so "render it now" found the cut the owner had
   * already replaced and truthfully reported nothing to do.
   */
  itemId: string;
  /**
   * The cut this card was emitted against, and the render tally at that moment.
   *
   * A card is a record of a decision offered at a point in time, but every card
   * for an item reads the same LIVE state — so an older one saw a newly staged
   * edit and offered to approve it, and after a reload an already-accepted card
   * came back with live buttons, because "accepted" was only browser state.
   *
   * When the item has moved past this stamp — a fork from an edit, a regenerate,
   * or a render this card did not start — the card is history and shows no
   * actions. Absent means unstamped (an older transcript), and those stay live
   * rather than being retired on a guess.
   */
  attemptId?: string;
  title?: string;
  serviceId?: string | null;
  minClipCount?: number;
  /**
   * Re-renders this post's edits had cost when this card was emitted.
   *
   * Two jobs, one number, and they agree: it is displayed (shown, never capped
   * — a considered edit is not indecision, but the cost should not be
   * invisible), and it is half the stamp that tells the card whether a render
   * has happened since it was offered.
   */
  renderCount?: number;
  /** Called after an approved edit is queued, so the host can react. */
  onApplied?: () => void;
  openEditor?: 'clips' | 'script';
  /**
   * Spelled out rather than imported because this prop crosses the wire on a
   * chat card. Kept in step with `authorityVideoStateValues` in
   * `@borradh-workspace/features/videos`, the two contract enums and the
   * api-client union — five copies, which is exactly how a rendered cut went
   * on reporting `ready_to_render`.
   */
  authorityState?:
    | 'draft'
    | 'awaiting_recording'
    | 'awaiting_voice'
    | 'ready_to_render'
    | 'rendering'
    | 'rendered'
    | null;
  proposedScript?: string;
  followLatest?: boolean;
}

function formatDuration(seconds: number | null): string | null {
  if (seconds === null || Number.isNaN(seconds) || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function SortableClip({
  clip,
  index,
  onPlay,
  onRemove,
  canRemove,
  disabled,
}: {
  clip: BatchItemClip;
  index: number;
  onPlay: () => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: clip.assetId, disabled });

  const duration = formatDuration(clip.duration);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-lg border bg-card p-2',
        isDragging && 'z-10 shadow-lg',
        clip.staged === 'remove' && 'opacity-50'
      )}
    >
      <button
        type="button"
        // The handle, not the row: the row itself has a play target and a
        // remove button on it, and making the whole thing draggable turns
        // every intended tap into a two-pixel drag.
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${clip.name}`}
        disabled={disabled}
        className="cursor-grab touch-none text-muted-foreground disabled:cursor-not-allowed active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </button>

      <span className="w-4 shrink-0 text-center text-xs tabular-nums text-muted-foreground">
        {index + 1}
      </span>

      <button
        type="button"
        onClick={onPlay}
        // No playable file yet means the footage is still transcoding. A play
        // button that opens an empty player is worse than one that is plainly
        // not ready.
        disabled={!clip.blobUrl}
        aria-label={`Play ${clip.name}`}
        className="group relative size-12 shrink-0 overflow-hidden rounded bg-muted disabled:cursor-not-allowed"
      >
        {/* `VideoThumbnail` on BOTH branches, never a bare <img>, and never a
            <video preload>. A stock clip usually has no `thumbnailUrl`, so an
            <img> alone painted an empty grey square for the whole list — and
            reaching for `preload="metadata"` instead would paint nothing in
            SAFARI, which is the documented reason that component exists: only
            autoPlay+muted then pause-on-first-play is reliable across
            browsers. It also owns the "no frame yet" fallback, so a tile is
            never a featureless square whichever branch it takes. */}
        {clip.thumbnailUrl ? (
          <VideoThumbnail
            src={clip.thumbnailUrl}
            isImage
            className="size-full"
          />
        ) : clip.blobUrl ? (
          <VideoThumbnail src={clip.blobUrl} className="size-full" />
        ) : (
          <span
            aria-hidden
            className="absolute inset-0 grid place-items-center text-muted-foreground/50"
          >
            <Film className="size-4" />
          </span>
        )}
        {clip.blobUrl ? (
          <span className="absolute inset-0 grid place-items-center bg-black/30 text-white opacity-0 transition-opacity group-hover:opacity-100">
            <Play className="size-4 fill-current" />
          </span>
        ) : null}
      </button>

      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium">{clip.name}</p>
        <p className="text-[11px] text-muted-foreground">
          {duration ?? (clip.blobUrl ? '—' : 'Still processing')}
        </p>
      </div>

      {clip.staged === 'added' ? (
        <Badge variant="secondary" className="shrink-0 text-[10px]">
          New
        </Badge>
      ) : null}

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        // A render needs at least one clip, and the server refuses to remove the
        // last. Blocking it here means the owner never arrives at a confirm that
        // can only fail.
        disabled={disabled || !canRemove}
        title={
          canRemove ? 'Remove this clip' : 'A video needs at least one clip'
        }
        aria-label={`Remove ${clip.name}`}
      >
        <Trash2 className="size-3.5" />
      </Button>
    </li>
  );
}

/**
 * The clips in a video, editable, with the approval that renders them.
 *
 * ONE CARD, not a list card plus a confirmation card beneath it. The thing
 * being approved is the list directly above the buttons — separating them puts
 * a boundary through the middle of a single decision, and the owner has to hold
 * "what I just arranged" in their head while looking at a summary of it.
 *
 * WHAT EACH BUTTON DOES
 *  - **Change Clips** opens the picker: the org's own uploads alongside the
 *    curated stock bank. Add and remove in bulk, by eye.
 *  - **Accept** renders. It is LIVE from the moment the card appears, because
 *    the clips arrive already assembled and approving them unchanged is the
 *    ordinary answer. Gating it behind "edit something first" is the shape of
 *    the original bug — being shown a finished video and made to fiddle with it
 *    before you are allowed to say yes. When edits ARE staged it commits all of
 *    them as ONE render: three changes cost one render, not three superseding
 *    each other mid-flight.
 *  - **Reject** discards the staged edits. The video is untouched.
 *
 * Editing the list never renders on its own. That decision is the owner's and it
 * is spent money; four of the five failed attempts at this feature rendered on
 * save, and one announced a render the server had declined to start.
 *
 * The list is read from the server rather than held here, so a reload shows the
 * same pending edit and Claire reads the same list the owner is looking at.
 * Local order is kept only for the moment between a drag finishing and the
 * refetch landing — without it the tiles snap back to the old order for a beat
 * and the drag reads as rejected.
 */
export function ClipListEditor({
  gateId,
  itemId,
  attemptId,
  title,
  serviceId,
  minClipCount = 1,
  renderCount: emittedRenderCount,
  onApplied,
  onCancelled,
  onDiscarded,
  openEditor,
  authorityState: emittedAuthorityState,
  proposedScript,
  followLatest = false,
}: ClipListEditorProps) {
  const {
    clips,
    pendingRelist,
    hasStagedEdits,
    textChanges,
    music,
    isLoading,
    attemptId: currentAttemptId,
    renderCount: currentRenderCount,
    serviceId: liveServiceId,
    authorityState: liveAuthorityState,
    narrationType,
    videoStatus,
    videoId,
    templateId,
    variationId,
    scriptText,
    refetch,
  } = useBatchItemClips(itemId);

  // Superseded: the item has a different cut now, or a render has been paid for
  // that this card did not start. Either way its offer is stale.
  //
  // Deliberately NOT keyed on `decision`, which is per-mount and vanishes on
  // reload. This is read from the server, so it survives one.
  const isHistorical =
    !followLatest &&
    isClipListCardHistorical({
      emittedAttemptId: attemptId,
      currentAttemptId,
      emittedRenderCount,
      currentRenderCount,
      openEditor,
      currentVideoStatus: videoStatus,
    });

  // What to SHOW. The live number is the truth about the item; the stamp is
  // only for deciding whether this card is stale.
  const renderCount = currentRenderCount;
  const { stageClips, isStaging } = useStageItemClips();
  const { stageMusicAsync, isStagingMusic } = useStageItemMusic();
  const { discardVideoEdits, isDiscarding } = useDiscardVideoEdits({
    onSuccess: () => {
      setDecision('rejected');
      onDiscarded?.();
    },
  });
  const { artifact: openPanelArtifact, autoOpenArtifact } = useArtifactPanel();
  const { applyVideoEdits, isApplying } = useApplyVideoEdits({
    onSuccess: (result) => {
      // `no_changes` means nothing was edited AND a cut already exists, so no
      // render ran. Spending the card's one decision on it would claim work
      // that never started; say so and leave Accept live.
      setOutcome(result.outcome);
      if (result.applied) {
        setDecision('accepted');
        // The id the SERVER started, which is the fork when an edit forked the
        // previous cut. Following the id we happened to be holding is how an
        // approved edit ends up watching a video that finished rendering
        // yesterday.
        setRenderingVideoId(result.videoId);
        onApplied?.();
      }
    },
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const [narrationOpen, setNarrationOpen] = useState(false);
  // A script save can fork an already-rendered cut. Keep the exact returned
  // video id for narration so a fast QR click cannot mint a token for the old
  // cut while the item query is still refetching.
  const [narrationVideoId, setNarrationVideoId] = useState<string | null>(null);
  const [narrationScriptText, setNarrationScriptText] = useState<string | null>(
    null
  );
  const [playing, setPlaying] = useState<BatchItemClip | null>(null);
  // The order as the owner last left it, held only until the refetch catches
  // up. Null means "whatever the server says", which is the normal state.
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  // Spent, not unmounted. A card that vanishes on click takes the only
  // acknowledgement of the click with it, which reads as "did that register?"
  const [decision, setDecision] = useState<'accepted' | 'rejected' | null>(
    null
  );
  // What the server said actually happened, so the card reports that rather
  // than what the click hoped for.
  const [outcome, setOutcome] = useState<
    'edits' | 'as_is' | 'no_changes' | null
  >(null);
  // The render this card started, so it can report the OUTCOME rather than the
  // request. "Re-rendering now" is a claim about the future; a card that says
  // it and then goes quiet leaves the owner watching a sentence.
  const [renderingVideoId, setRenderingVideoId] = useState<string | null>(null);
  const musicEditorEnabled = useFlag(CLAIRE_CONTENT_MUSIC_EDITOR_FLAG);

  const openedEditorRef = useRef(false);
  useEffect(() => {
    if (openEditor === 'clips' && !openedEditorRef.current) {
      openedEditorRef.current = true;
      setPickerOpen(true);
    }
  }, [openEditor]);

  const authorityState = liveAuthorityState ?? emittedAuthorityState ?? null;
  // A finished cut with nothing staged has nothing to render.
  //
  // `applyBatchItemVideoEdits` already answers that press with `no_changes` —
  // no render is spent — so the cost is not money, it is a live primary button
  // that does nothing, on the one card where every other control does. Staged
  // edits are the exception and the reason this is not in
  // `authorityRenderBlockReason`: editing a rendered video is the supported
  // flow, it forks, and only this component knows whether anything is staged.
  const alreadyRendered =
    (authorityState === 'rendered' || videoStatus === 'ready') &&
    !hasStagedEdits;
  const renderBlockReason =
    authorityState === 'draft'
      ? 'Add and save the script before rendering'
      : authorityState === 'awaiting_recording'
        ? 'Choose an AI voiceover or attach a recording to enable Accept.'
        : authorityState === 'awaiting_voice'
          ? 'Choose an AI voiceover or attach a recording to enable Accept.'
          : authorityState === 'rendering' ||
              videoStatus === 'queued' ||
              videoStatus === 'processing'
            ? 'This video is already rendering'
            : alreadyRendered
              ? 'This cut is already rendered — edit it to make a new version'
              : null;
  const needsNarrationChoice =
    authorityState === 'draft' ||
    authorityState === 'awaiting_recording' ||
    authorityState === 'awaiting_voice';
  // The one thing the owner must do next, promoted to a real button.
  //
  // It used to be a `variant="link"` inside an 11px helper sentence while
  // Accept — which cannot fire until this is answered — kept the filled
  // primary styling. The card therefore had a dead control dressed as its call
  // to action and its actual call to action dressed as body copy. `draft` is
  // excluded because there is no script yet to narrate.
  const showNarrationCta =
    renderBlockReason !== null &&
    needsNarrationChoice &&
    authorityState !== 'draft';
  const willGenerateAiVoiceover =
    authorityState === 'ready_to_render' && narrationType === 'ai_voiceover';
  const hasAttachedRecording =
    authorityState === 'ready_to_render' && narrationType === 'recorded';
  // Always the server's revision. `patchContent` writes the script it proposes
  // before the card is built, so the stored text IS the proposal — and reading
  // the card's stamped copy instead would resurrect it over any later edit,
  // silently reverting a saved script and sending the wrong teleprompter copy
  // to a QR token. `proposedScript` remains only as a first-paint fallback for
  // a card emitted before that write existed.
  const currentScriptText = scriptText.trim()
    ? scriptText
    : (proposedScript ?? '');

  const previewVideoId =
    renderingVideoId ??
    (followLatest &&
    !hasStagedEdits &&
    (videoStatus === 'ready' ||
      videoStatus === 'queued' ||
      videoStatus === 'processing')
      ? videoId
      : null);

  // Shares the panel's query key on purpose: one poll feeds both, and the card
  // and the panel can never disagree about whether the render has finished.
  const { data: renderedVideo } = useQuery({
    queryKey: queryKeys.content.video(previewVideoId ?? ''),
    queryFn: () =>
      apiClient.get<{
        status: string;
        title?: string | null;
        thumbnailUrl?: string | null;
      }>(`videos/${previewVideoId}`),
    enabled: previewVideoId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'processing' ? 5_000 : false;
    },
  });
  const renderStatus = renderedVideo?.status;
  const pendingLabel =
    (renderStatus ?? videoStatus) === 'queued' ? 'Queued…' : 'Rendering…';
  const renderDone = renderStatus === 'ready';
  const renderFailed = renderStatus === 'failed';

  // Open the panel when the render LANDS, not when it was requested.
  //
  // Half the screen taken to show a pulsing rectangle is half the screen taken
  // to say "not yet", at the moment the owner is most likely to still be
  // typing. The artifact row in the chat already reports that it is rendering,
  // and it is small. Opening once, on completion, is the only moment the panel
  // has something to show.
  //
  // Once, via the ref: reopening a panel the owner deliberately closed is the
  // kind of helpfulness that has to be fought.
  // An ad open in the panel that is waiting on THIS video as its creative.
  const openAdWaitingOnThis =
    openPanelArtifact?.kind === 'ad' &&
    openPanelArtifact.creativeId === previewVideoId;
  const openedRef = useRef(false);
  useEffect(() => {
    if (renderDone && previewVideoId && !openedRef.current) {
      openedRef.current = true;
      // The open ad is waiting on this very video; it fills in there, so
      // taking the panel over would swap the ad for its own picture. Spend
      // the landing quietly (see GraphicStatusCard). An unrelated open ad
      // does not get in the way.
      if (openAdWaitingOnThis) return;
      autoOpenArtifact({ kind: 'video', id: previewVideoId, itemId });
    }
  }, [
    renderDone,
    previewVideoId,
    autoOpenArtifact,
    itemId,
    openAdWaitingOnThis,
  ]);

  // The server is the authority the moment it answers. Dropping the local
  // order on every fresh list is what keeps a stale drag from outliving the
  // edit that superseded it — including one made in another tab.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sync to server list identity
  useEffect(() => {
    setLocalOrder(null);
  }, [clips.map((clip) => clip.assetId).join(',')]);

  const ordered = useMemo(() => {
    if (!localOrder) return clips;
    const byId = new Map(clips.map((clip) => [clip.assetId, clip]));
    return localOrder
      .map((assetId) => byId.get(assetId))
      .filter((clip): clip is BatchItemClip => Boolean(clip));
  }, [clips, localOrder]);

  // Every reason Accept cannot fire, in one place.
  //
  // An empty list cannot render, and letting Accept through would hand it to
  // the export-time fill — which DOES draw on the ambient pool, quietly
  // reintroducing the footage this path just refused to choose. Blocking here
  // keeps that decision the owner's. Whatever the reason, a button that cannot
  // fire must not keep the filled primary styling that marks it as the card's
  // call to action.
  const acceptBlocked =
    ordered.length < minClipCount || renderBlockReason !== null;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Without a distance threshold a tap on the handle registers as a
      // zero-length drag and swallows the click.
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const busy = isStaging || isStagingMusic || isApplying || isDiscarding;

  const saveMusic = async (choice: {
    trackId: string | null;
    url: string | null;
    volume: number;
    startSec?: number;
  }) => {
    try {
      await stageMusicAsync({ itemId, ...choice });
      setDecision(null);
      setOutcome(null);
      setMusicOpen(false);
    } catch {
      // The mutation owns the error toast; leave the dialog open for retry.
    }
  };

  const stage = (assetIds: string[]) => {
    setLocalOrder(assetIds);
    // A fresh edit supersedes a spent decision — the buttons go live again
    // rather than staying on "Rejected" over a list that has since changed,
    // and "already the current cut" stops being true the moment it changes.
    setDecision(null);
    setOutcome(null);
    stageClips({ itemId, assetIds });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = ordered.map((clip) => clip.assetId);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    stage(arrayMove(ids, from, to));
  };

  const handleRemove = (assetId: string) => {
    stage(ordered.map((clip) => clip.assetId).filter((id) => id !== assetId));
  };

  const reject = () => {
    if (busy) return;
    if (hasStagedEdits) discardVideoEdits(itemId);
    else {
      setDecision('rejected');
      onCancelled?.();
    }
  };
  const awaitingDecision =
    !isLoading &&
    !isHistorical &&
    decision === null &&
    authorityState !== 'rendering' &&
    videoStatus !== 'queued' &&
    videoStatus !== 'processing' &&
    !alreadyRendered;
  // Choosing a voice does not spend the approval. Keep the same chooser
  // available until rendering so the owner can use a recording instead.
  const narrationActionLabel = showNarrationCta
    ? 'Choose narration'
    : awaitingDecision && authorityState === 'ready_to_render' && videoId
      ? 'Change narration'
      : null;
  const composerGate = awaitingDecision
    ? {
        sentence:
          renderBlockReason ??
          (hasStagedEdits
            ? 'Apply these changes to the video?'
            : 'Render this video?'),
        onAccept: () => {
          if (!busy && !acceptBlocked) applyVideoEdits(itemId);
        },
        onReject: reject,
        acceptLabel: isApplying
          ? 'Rendering…'
          : videoStatus === 'failed'
            ? 'Retry render'
            : 'Accept',
        rejectLabel: 'Reject',
        acceptDisabled: acceptBlocked,
        acceptDisabledReason: [
          renderBlockReason,
          ordered.length < minClipCount
            ? `Choose at least ${minClipCount} ${minClipCount === 1 ? 'clip' : 'clips'} using Change to enable Accept.`
            : null,
        ]
          .filter(Boolean)
          .join(' '),
        allowMessage: false,
        busy,
        onEdit: () => setPickerOpen(true),
        editLabel: 'Change',
        additionalActions: [
          ...(musicEditorEnabled && videoId
            ? [
                {
                  label: 'Edit music',
                  onClick: () => setMusicOpen(true),
                },
              ]
            : []),
          ...(authorityState
            ? [
                {
                  label: 'Edit script',
                  onClick: () => setScriptOpen(true),
                  disabled: !videoId,
                },
              ]
            : []),
          ...(narrationActionLabel
            ? [
                {
                  label: narrationActionLabel,
                  onClick: () => setNarrationOpen(true),
                },
              ]
            : []),
        ],
      }
    : null;

  const inComposer = useHasComposerGate();
  useRegisterComposerGate(
    gateId ?? `content-clips:${itemId}:${attemptId ?? 'live'}`,
    composerGate
      ? {
          ...composerGate,
          content: (
            <div className="space-y-3">
              <div className="flex gap-2 overflow-x-auto">
                {ordered.map((clip, index) => (
                  <button
                    key={clip.assetId}
                    type="button"
                    aria-label={`Preview clip ${index + 1}`}
                    className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-muted"
                    onClick={() => setPlaying(clip)}
                  >
                    {clip.thumbnailUrl ? (
                      <VideoThumbnail
                        src={clip.thumbnailUrl}
                        isImage
                        className="size-full"
                      />
                    ) : clip.blobUrl ? (
                      <VideoThumbnail
                        src={clip.blobUrl}
                        className="size-full"
                      />
                    ) : (
                      <Film className="mx-auto size-5" />
                    )}
                  </button>
                ))}
              </div>
              <DecisionFooter {...composerGate} />
            </div>
          ),
        }
      : null
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Loading the clips…
      </div>
    );
  }

  if (decision === 'rejected') {
    return (
      <p className="text-xs text-muted-foreground">
        Discarded. The video is unchanged.
      </p>
    );
  }
  if (decision === 'accepted') {
    return renderingVideoId ? (
      <ArtifactRow
        artifact={{ kind: 'video', id: renderingVideoId, itemId }}
        title={renderedVideo?.title ?? title ?? 'Video'}
        subtitle={
          renderFailed
            ? 'Render failed'
            : renderDone
              ? 'Video · MP4'
              : pendingLabel
        }
        thumbnailUrl={renderedVideo?.thumbnailUrl}
        pending={!renderDone && !renderFailed}
      />
    ) : (
      <p className="text-xs text-muted-foreground">
        {outcome === 'as_is'
          ? 'Accepted these clips.'
          : 'Accepted your changes.'}
      </p>
    );
  }

  return (
    <>
      {inComposer ? (
        videoId && !awaitingDecision ? (
          <ArtifactRow
            artifact={{ kind: 'video', id: videoId, itemId }}
            title={title ?? 'Video'}
            // This row opens the live video from the item query. A spent
            // approval does not make that video an earlier version.
            subtitle={
              videoStatus === 'ready'
                ? 'Video · MP4'
                : videoStatus === 'queued' || videoStatus === 'processing'
                  ? 'Rendering…'
                  : videoStatus === 'failed'
                    ? 'Render failed'
                    : 'Video'
            }
            pending={videoStatus === 'queued' || videoStatus === 'processing'}
          />
        ) : null
      ) : (
        <div className="w-full rounded-xl border bg-background p-3 sm:max-w-lg">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="truncate text-xs font-medium">{title ?? 'Clips'}</p>
            <p className="shrink-0 text-[11px] text-muted-foreground">
              {ordered.length} clip{ordered.length === 1 ? '' : 's'}
              {pendingRelist ? ' · edited' : ''}
            </p>
          </div>

          {/* Not "nothing here yet" — a statement about the footage bank.
          Nothing matched this service, and the alternative was to fill the list
          from the vertical's ambient pool to reach a count, which is how a
          dentist in red scrubs ended up presented as clip 3 of a microneedling
          video. An empty list plus the picker is the honest version. */}
          {ordered.length === 0 ? (
            <p className="rounded-lg border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
              No footage matched this service yet — pick some with Change Clips.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={ordered.map((clip) => clip.assetId)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="flex flex-col gap-1.5">
                  {ordered.map((clip, index) => (
                    <SortableClip
                      key={clip.assetId}
                      clip={clip}
                      index={index}
                      onPlay={() => setPlaying(clip)}
                      onRemove={() => handleRemove(clip.assetId)}
                      canRemove={ordered.length > minClipCount}
                      disabled={busy || decision !== null || isHistorical}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}

          {textChanges.length > 0 || music.staged ? (
            <ul className="mt-2 space-y-0.5">
              {textChanges.map((change) => (
                <li key={change} className="text-[11px] text-muted-foreground">
                  · {change}
                </li>
              ))}
              {music.staged ? (
                <li className="text-[11px] text-muted-foreground">
                  · Music change staged
                </li>
              ) : null}
            </ul>
          ) : null}

          {followLatest && decision === null && previewVideoId ? (
            <ArtifactRow
              className="mt-3"
              artifact={{ kind: 'video', id: previewVideoId, itemId }}
              title={renderedVideo?.title ?? title ?? 'Video'}
              subtitle={renderDone ? 'Video · MP4' : pendingLabel}
              thumbnailUrl={renderedVideo?.thumbnailUrl}
            />
          ) : null}

          {isHistorical && decision === null ? (
            <p className="mt-3 text-[11px] text-muted-foreground">
              This version has been superseded.
            </p>
          ) : decision === null ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/* Only when there IS something to discard.
              This was previously rendered always and merely disabled, to keep
              the row from reflowing. The cost of that was a permanently dead
              grey control on every freshly-proposed card — the owner's first
              impression of the card was a button that does nothing, and
              "Reject" never said what it would reject. A staged edit is the
              only thing this can undo, so it appears with one. */}
              {hasStagedEdits ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={reject}
                  disabled={busy}
                >
                  <X className="size-3.5" />
                  Discard changes
                </Button>
              ) : awaitingDecision ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={reject}
                  disabled={busy}
                >
                  Cancel
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setPickerOpen(true)}
                disabled={busy}
              >
                <Pencil className="size-3.5" />
                Change Clips
              </Button>
              {musicEditorEnabled && videoId ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setMusicOpen(true)}
                  disabled={busy}
                >
                  <Music2 className="size-3.5" />
                  Edit music
                </Button>
              ) : null}
              {authorityState ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setScriptOpen(true)}
                  disabled={busy || !videoId}
                >
                  <Pencil className="size-3.5" />
                  Edit Script
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                // Blocked means this is not the action to take, so it must not go
                // on wearing the filled primary styling: at 50% opacity a primary
                // button still reads as the one thing to press, and the owner's
                // real next step is beside it.
                variant={acceptBlocked ? 'outline' : 'default'}
                className="gap-1.5"
                onClick={() => applyVideoEdits(itemId)}
                disabled={busy || acceptBlocked}
                title={
                  renderBlockReason ??
                  (ordered.length < minClipCount
                    ? 'Add at least one clip first'
                    : hasStagedEdits
                      ? 'Render this version'
                      : 'Render these clips as they are')
                }
              >
                {isApplying ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Check className="size-3.5" />
                )}
                {isApplying
                  ? 'Rendering…'
                  : videoStatus === 'failed'
                    ? 'Retry render'
                    : 'Accept'}
              </Button>
              {/* Last in the row on purpose. The row wraps, and the primary
              action has to be the one that survives on its own line — with
              this before Accept, a narrow card wrapped the DISABLED Accept
              onto a line by itself, which is the same "dead control presented
              as a step" this card just stopped doing. */}
              {narrationActionLabel ? (
                <Button
                  type="button"
                  size="sm"
                  variant={showNarrationCta ? 'default' : 'outline'}
                  className="gap-1.5"
                  onClick={() => setNarrationOpen(true)}
                  disabled={busy}
                >
                  <Mic className="size-3.5" />
                  {narrationActionLabel}
                </Button>
              ) : null}
              {/* One line, and only when it says something the buttons do not.
              With `Choose narration` promoted to a button, the sentence that
              used to ask the same question beside it was the button's label
              written twice. */}
              {showNarrationCta ? null : renderBlockReason ? (
                <span className="w-full text-[11px] text-muted-foreground">
                  {authorityState === 'draft'
                    ? 'Save the script, then choose how to narrate it.'
                    : `${renderBlockReason}.`}
                </span>
              ) : willGenerateAiVoiceover ? (
                <span className="w-full text-[11px] text-muted-foreground">
                  AI voiceover selected. Click Accept to render.
                </span>
              ) : hasAttachedRecording ? (
                <span className="w-full text-[11px] text-muted-foreground">
                  Your recording is attached. Click Accept to render.
                </span>
              ) : null}
              {/* Accept was pressed, nothing was edited, and a cut already exists —
              so no render ran. Saying that beats spending a render to reproduce
              the frames already on screen, and beats a button that looks
              broken. */}
              {outcome === 'no_changes' ? (
                <span className="w-full text-[11px] text-muted-foreground">
                  This is already the current cut — change something to get a
                  new one.
                </span>
              ) : null}
            </div>
          ) : null}

          {renderCount > 0 ? (
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {renderCount} render{renderCount === 1 ? '' : 's'} used so far.
            </p>
          ) : null}
        </div>
      )}
      <ClipPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selectedIds={ordered.map((clip) => clip.assetId)}
        // The picker hands back the whole selection, which is exactly what gets
        // staged. It does not preserve the order of the list it was opened with
        // for clips it did not touch, so the tiles are the owner's last word on
        // membership and the drag handles remain their last word on order.
        onConfirm={stage}
        serviceId={liveServiceId ?? serviceId ?? null}
        minCount={minClipCount}
      />
      {musicEditorEnabled ? (
        <ContentMusicEditor
          open={musicOpen}
          onOpenChange={setMusicOpen}
          value={{
            trackId: music.trackId,
            url: music.url,
            volume: music.volume,
            ...(music.startSec !== null ? { startSec: music.startSec } : {}),
          }}
          onSave={saveMusic}
          saving={isStagingMusic}
        />
      ) : null}

      <VideoPreviewDialog
        open={playing !== null}
        onOpenChange={(next) => {
          if (!next) setPlaying(null);
        }}
        videoUrl={playing?.blobUrl ?? ''}
        thumbnailUrl={playing?.thumbnailUrl ?? undefined}
        title={playing?.name ?? 'Clip'}
      />
      {videoId ? (
        <ScriptEditorDialog
          open={scriptOpen}
          onOpenChange={setScriptOpen}
          videoId={videoId}
          templateId={templateId}
          variationId={variationId}
          serviceId={liveServiceId ?? serviceId ?? null}
          narrationMode={narrationType}
          scriptText={currentScriptText}
          onSaved={(savedScriptText, savedVideoId) => {
            setNarrationScriptText(savedScriptText);
            setNarrationVideoId(savedVideoId);
            void refetch();
          }}
        />
      ) : null}
      {videoId ? (
        <NarrationChoiceDialog
          open={narrationOpen}
          onOpenChange={setNarrationOpen}
          videoId={narrationVideoId ?? videoId}
          variationId={variationId}
          scriptText={narrationScriptText ?? currentScriptText}
          onSaved={async () => {
            await refetch();
            setNarrationVideoId(null);
          }}
        />
      ) : null}
    </>
  );
}
